import { createContext, useContext, useEffect, useRef, ReactNode } from 'react'
import { useConvexAuth, useQuery, useMutation } from 'convex/react'
import { useAuthActions } from '@convex-dev/auth/react'
import { api } from '../../convex/_generated/api'
import { getAttribution, getAttributionTouch, clearAttribution } from '@/lib/attribution'
import { getVisitorId } from '@/lib/analytics'
import { isElectron, setCliToken } from '@/lib/electron'

interface UserProfile {
  _id: string
  userId?: string
  email: string
  name?: string
  avatarUrl?: string
  isAnonymous?: boolean
  deviceId?: string
  isAdmin?: boolean
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  planStatus?: string
  creditsUsedThisPeriod: number
  creditsUsedLifetime: number
  createdAt?: number
  deletionRequestedAt?: number
  supabaseUserId?: string
  nuxCompleted?: boolean
  nuxWorkflowId?: string
  nuxPendingAction?: string
  isSuspended?: boolean
  hasDesktopApp?: boolean
  communityJoinedAt?: number
  communityLastSeenAt?: number
}

interface AuthContextType {
  user: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean
  signOut: () => Promise<void>
}

const DEVICE_ID_KEY = 'footage_device_id'

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(DEVICE_ID_KEY, id)
  }
  return id
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoading: isAuthLoading, isAuthenticated } = useConvexAuth()
  const { signOut: convexSignOut } = useAuthActions()
  const upsertProfile = useMutation(api.userProfiles.upsertProfile)
  const generateDesktopCliToken = useMutation(api.cliApi.generateDesktopCliToken)
  const cliTokenProvisionedRef = useRef(false)

  const profile = useQuery(
    api.userProfiles.getCurrent,
    isAuthenticated ? {} : 'skip'
  )

  // Create profile when authenticated but no profile exists
  useEffect(() => {
    if (isAuthenticated && profile === null) {
      const attribution = getAttribution()
      const touch = getAttributionTouch()
      const visitorId = getVisitorId() ?? undefined
      upsertProfile({
        ...(attribution ? {
          attributionChannel: attribution.channel,
          attributionReferrer: attribution.referrer,
        } : {}),
        ...(touch ? { attributionTouch: touch } : {}),
        visitorId,
        deviceId: getOrCreateDeviceId(),
      }).catch(console.error)
    }
  }, [isAuthenticated, profile, upsertProfile])

  // Clear attribution once a real profile exists
  useEffect(() => {
    if (profile) {
      clearAttribution()
    }
  }, [profile])

  // Auto-provision CLI token for desktop app so tool-exec can authenticate
  useEffect(() => {
    if (!isAuthenticated || !profile || profile.isAnonymous) return
    if (!isElectron()) return
    if (cliTokenProvisionedRef.current) return
    cliTokenProvisionedRef.current = true
    generateDesktopCliToken({}).then(
      (result) => setCliToken(result.token),
      (err) => console.error('Failed to provision desktop CLI token:', err),
    )
  }, [isAuthenticated, profile, generateDesktopCliToken])

  const signingOut = useRef(false)
  const isLoading = signingOut.current || isAuthLoading || (isAuthenticated && !profile)

  const handleSignOut = async () => {
    signingOut.current = true
    localStorage.removeItem('is_impersonating')
    await convexSignOut()
    window.location.href = '/'
  }

  return (
    <AuthContext.Provider
      value={{
        user: profile ?? null,
        isLoading,
        isAuthenticated,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
